import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save, Zap, Shield, Building2, DollarSign, Globe, Wind, Server, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';

interface LocationEditModalProps {
  facility: any;
  onClose: () => void;
  onSave: (facility: any) => Promise<void>;
}

export function LocationEditModal({ facility, onClose, onSave }: LocationEditModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      name: facility.name,
      location: facility.location,
      country: facility.country,
      capacity: facility.capacity,
      powerSource: facility.powerSource,
      powerCost: facility.powerCost,
      minOrder: facility.minOrder,
      features: facility.features?.join('\n'),
      securityLevel: facility.securityLevel,
      uptime: facility.uptime,
      qualityScore: facility.qualityScore,
      certifications: facility.certifications?.join('\n'),
      description: facility.description,
      status: facility.status,
      amenities: facility.amenities?.join('\n'),
      powerDetails: {
        sources: facility.powerDetails?.sources?.join('\n'),
        backupType: facility.powerDetails?.backupType,
        gridConnection: facility.powerDetails?.gridConnection
      },
      cooling: {
        type: facility.cooling?.type,
        efficiency: facility.cooling?.efficiency,
        capacity: facility.cooling?.capacity
      },
      measureX: {
        ready: facility.measureX?.ready,
        version: facility.measureX?.version,
        features: facility.measureX?.features?.join('\n')
      }
    }
  });

  const onSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);
      await onSave({
        ...facility,
        ...data,
        features: data.features.split('\n').filter(Boolean),
        certifications: data.certifications.split('\n').filter(Boolean),
        amenities: data.amenities.split('\n').filter(Boolean),
        powerDetails: {
          ...data.powerDetails,
          sources: data.powerDetails.sources.split('\n').filter(Boolean)
        },
        measureX: {
          ...data.measureX,
          features: data.measureX.features.split('\n').filter(Boolean)
        }
      });
    } catch (error) {
      console.error('Error saving facility:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
        />

        {/* Modal panel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl w-full"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Facility
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Basic Information
                  </h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      {...register('name', { required: 'Name is required' })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <input
                      type="text"
                      {...register('location', { required: 'Location is required' })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <input
                      type="text"
                      {...register('country', { required: 'Country is required' })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Power & Capacity */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Power & Capacity
                  </h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Capacity</label>
                    <input
                      type="text"
                      {...register('capacity', { required: 'Capacity is required' })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Power Source</label>
                    <input
                      type="text"
                      {...register('powerSource', { required: 'Power source is required' })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Power Cost (kWh)</label>
                    <input
                      type="number"
                      step="0.001"
                      {...register('powerCost', { required: 'Power cost is required' })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Minimum Order</label>
                    <input
                      type="number"
                      {...register('minOrder', { required: 'Minimum order is required' })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Features & Security */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Features & Security
                  </h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Features (one per line)</label>
                    <textarea
                      {...register('features')}
                      rows={4}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Security Level</label>
                    <select
                      {...register('securityLevel')}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="standard">Standard</option>
                      <option value="advanced">Advanced</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Certifications (one per line)</label>
                    <textarea
                      {...register('certifications')}
                      rows={3}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Status & Performance */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Status & Performance
                  </h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      {...register('status')}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="active">Active</option>
                      <option value="coming-soon">Coming Soon</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Uptime (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      max="100"
                      {...register('uptime')}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Quality Score (1-5)</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      {...register('qualityScore')}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}